// F-085 — Appels de fonds et décomptes du syndic, par immeuble et par lot.
import { useState } from "react";
import { del, post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { date, today } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Stat, StatusChip, Table, Tabs, Textarea, fieldError } from "@core/ui";
import { Amount, BuildingSelect, DocLink, PAYMENT_METHODS, PropertySelect, currentYear, required, useAction, useRefs } from "./shared";

type Call = {
  id: string;
  building_id: string;
  building_label: string;
  property_id: string | null;
  property_label: string | null;
  fiscal_year: string;
  period_label: string;
  kind: string;
  kind_label: string;
  call_on: string;
  due_on: string | null;
  amount_cents: number;
  recoverable_estimate_cents: number;
  document_id: string | null;
  document_filename: string | null;
  notes: string;
  operation_id: string | null;
  paid_on: string | null;
};

type Line = { id?: string; label: string; category: string; amount_cents: number; recoverable_cents: number; recoverable_basis?: string; source_ref: string; category_label?: string; proposed_recoverable_cents?: number; hint?: string };
type Statement = {
  id: string;
  building_id: string;
  building_label: string;
  property_id: string | null;
  property_label: string | null;
  fiscal_year: string;
  period_start: string;
  period_end: string;
  document_id: string | null;
  document_filename: string | null;
  status: "a_valider" | "valide";
  validated_at: string | null;
  notes: string;
  lines: Line[];
  summary: { total_cents: number; recoverable_cents: number; non_recoverable_cents: number; provisions_called_cents: number; balance_cents: number };
};

export function CondoPage() {
  const org = useOrg();
  const refs = useRefs();
  const [tab, setTab] = useState<"appels" | "decomptes">("appels");
  const [building, setBuilding] = useState("");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Immeubles"
        title="Copropriété et syndic"
        intro="Appels de fonds (avances versées au syndic) et décomptes annuels (charges réelles). La part récupérable auprès du locataire se fixe au décompte, après votre validation."
      />
      <div className="finances-filtres">
        <BuildingSelect refs={refs.data} value={building} onChange={setBuilding} empty="Tous les immeubles" />
      </div>
      <Tabs value={tab} onChange={setTab} items={[{ value: "appels", label: "Appels de fonds" }, { value: "decomptes", label: "Décomptes annuels" }]} />
      {tab === "appels" ? <Calls building={building} canEdit={org.can("finance")} /> : <Statements building={building} canEdit={org.can("finance")} />}
    </div>
  );
}

function Calls({ building, canEdit }: { building: string; canEdit: boolean }) {
  const org = useOrg();
  const q = useGet<{ items: Call[]; kinds: { code: string; label: string }[]; totals: { called_cents: number; paid_cents: number; unpaid_cents: number }; notice: string }>(org.api(`/condo/calls${building ? `?building_id=${building}` : ""}`));
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<Call | null>(null);
  const a = useAction();
  return (
    <div className="stack">
      {q.data ? <Notice>{q.data.notice}</Notice> : null}
      <div className="row-between">
        {q.data ? (
          <div className="stats" style={{ flex: 1 }}>
            <Stat label="Appelé" value={<Amount cents={q.data.totals.called_cents} />} />
            <Stat label="Réglé" value={<Amount cents={q.data.totals.paid_cents} />} />
            <Stat label="Reste à régler" value={<Amount cents={q.data.totals.unpaid_cents} />} />
          </div>
        ) : null}
      </div>
      {canEdit ? (
        <div>
          <Button onClick={() => setAdding(true)}>Enregistrer un appel de fonds</Button>
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? a.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun appel de fonds enregistré." /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Appels de fonds">
          <thead>
            <tr>
              <th>Période</th>
              <th>Nature</th>
              <th>Lot</th>
              <th>Échéance</th>
              <th className="right">Montant</th>
              <th className="right">Part récupérable estimée</th>
              <th>Règlement</th>
              {canEdit ? <th><span className="sr-only">Actions</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.period_label}
                  <span className="finances-sous">Exercice {c.fiscal_year} · {c.building_label}</span>
                  {c.document_id ? <DocLink id={c.document_id} name={c.document_filename} /> : null}
                </td>
                <td>{c.kind_label}</td>
                <td>{c.property_label ?? "Immeuble"}</td>
                <td>{date(c.due_on ?? c.call_on)}</td>
                <td className="right"><Amount cents={c.amount_cents} /></td>
                <td className="right"><Amount cents={c.recoverable_estimate_cents} /><span className="finances-sous">estimation, non refacturable en l’état</span></td>
                <td>{c.operation_id ? <StatusChip status="conforme" label={`Réglé le ${date(c.paid_on)}`} /> : <Badge tone="attention">À régler</Badge>}</td>
                {canEdit ? (
                  <td>
                    <div className="finances-actions">
                      {!c.operation_id ? (
                        <>
                          <Button small variant="secondaire" onClick={() => setPaying(c)}>Enregistrer le règlement</Button>
                          <Button small variant="discret" onClick={() => a.run(() => del(org.api(`/condo/calls/${c.id}`)), { success: "Appel supprimé.", refresh: [org.api("/condo")] })}>Supprimer</Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {adding ? <CallModal kinds={q.data?.kinds ?? []} defaultBuilding={building} onClose={() => setAdding(false)} /> : null}
      {paying ? <PayModal call={paying} onClose={() => setPaying(null)} /> : null}
    </div>
  );
}

function CallModal({ kinds, defaultBuilding, onClose }: { kinds: { code: string; label: string }[]; defaultBuilding: string; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const [f, setF] = useState({
    building_id: defaultBuilding,
    property_id: "",
    fiscal_year: String(currentYear()),
    period_label: "",
    kind: "provisions",
    call_on: today(),
    due_on: "",
    amount_cents: null as number | null,
    recoverable_estimate_cents: null as number | null,
    notes: "",
  });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Appel de fonds"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const missing = required([
                [!!f.building_id, "building_id", "Choisissez l’immeuble."],
                [!!f.call_on, "call_on", "Indiquez la date d’appel."],
              ]);
              if (missing) return a.setError(missing);
              const body = { ...f, property_id: f.property_id || null, due_on: f.due_on || null, amount_cents: f.amount_cents ?? 0, recoverable_estimate_cents: f.recoverable_estimate_cents ?? 0 };
              const r = await a.run(() => post(org.api("/condo/calls"), body), { success: "Appel de fonds enregistré.", refresh: [org.api("/condo")] });
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <div className="form-row">
          <BuildingSelect refs={refs.data} value={f.building_id} error={fieldError(a.error, "building_id")} onChange={(v) => setF({ ...f, building_id: v, property_id: "" })} empty="Choisir…" />
          <PropertySelect refs={refs.data} buildingId={f.building_id} value={f.property_id} error={fieldError(a.error, "property_id")} onChange={(v) => setF({ ...f, property_id: v })} label="Lot" empty="Tout l’immeuble" />
        </div>
        <div className="form-row">
          <Input label="Exercice" required value={f.fiscal_year} error={fieldError(a.error, "fiscal_year")} onChange={(e) => setF({ ...f, fiscal_year: e.target.value })} />
          <Input label="Période appelée" required placeholder="Ex. 4e trimestre 2026" value={f.period_label} error={fieldError(a.error, "period_label")} onChange={(e) => setF({ ...f, period_label: e.target.value })} />
          <Select label="Nature" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={kinds.map((k) => ({ value: k.code, label: k.label }))} />
        </div>
        <div className="form-row">
          <Input label="Date de l’appel" type="date" value={f.call_on} error={fieldError(a.error, "call_on")} onChange={(e) => setF({ ...f, call_on: e.target.value })} />
          <Input label="Échéance" type="date" value={f.due_on} error={fieldError(a.error, "due_on")} onChange={(e) => setF({ ...f, due_on: e.target.value })} />
        </div>
        <div className="form-row">
          <MoneyInput label="Montant appelé" required cents={f.amount_cents} error={fieldError(a.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
          <MoneyInput label="Part récupérable estimée" cents={f.recoverable_estimate_cents} help="Estimation seulement : la part réelle se fixe au décompte." error={fieldError(a.error, "recoverable_estimate_cents")} onChange={(c) => setF({ ...f, recoverable_estimate_cents: c })} />
        </div>
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}

function PayModal({ call, onClose }: { call: Call; onClose: () => void }) {
  const org = useOrg();
  const [f, setF] = useState({ paid_on: today(), payment_method: "virement" });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Règlement de l’appel de fonds"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={async () => {
            const r = await a.run(() => post(org.api(`/condo/calls/${call.id}/pay`), f), { success: "Règlement enregistré dans les dépenses.", refresh: [org.api("/condo"), org.api("/finance")] });
            if (r) onClose();
          }}>Enregistrer le règlement</Button>
        </>
      }
    >
      <div className="stack">
        <p>
          {call.period_label} — <Amount cents={call.amount_cents} />. Une dépense « charges de copropriété » est créée une seule fois, avec une part récupérable à déterminer au décompte.
        </p>
        <ErrorBox error={a.error} />
        <div className="form-row">
          <Input label="Date de paiement" type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} />
          <Select label="Mode de règlement" value={f.payment_method} options={PAYMENT_METHODS} onChange={(e) => setF({ ...f, payment_method: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

function Statements({ building, canEdit }: { building: string; canEdit: boolean }) {
  const org = useOrg();
  const q = useGet<{ items: Statement[]; line_categories: { code: string; label: string; default_ratio_bp: number; hint: string }[] }>(org.api(`/condo/statements${building ? `?building_id=${building}` : ""}`));
  const [editing, setEditing] = useState<Statement | "new" | null>(null);
  const a = useAction();
  return (
    <div className="stack">
      <Notice>
        Les parts récupérables sont <strong>proposées</strong> d’après la nature de chaque poste (liste des charges récupérables du décret n° 87-713). Vous les validez ou les corrigez ; seul un décompte validé s’exporte pour la régularisation des charges.
      </Notice>
      {canEdit ? (
        <div>
          <Button onClick={() => setEditing("new")}>Saisir un décompte</Button>
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? a.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun décompte enregistré." /> : null}
      {(q.data?.items ?? []).map((s) => (
        <Card
          key={s.id}
          title={`Exercice ${s.fiscal_year} — ${s.building_label}${s.property_label ? ` · ${s.property_label}` : ""}`}
          actions={s.status === "valide" ? <StatusChip status="conforme" label={`Validé le ${date(s.validated_at)}`} /> : <StatusChip status="a_confirmer" label="Parts récupérables à valider" />}
        >
          <div className="stats">
            <Stat label="Charges réelles" value={<Amount cents={s.summary.total_cents} />} />
            <Stat label="Part récupérable" value={<Amount cents={s.summary.recoverable_cents} />} />
            <Stat label="Provisions appelées" value={<Amount cents={s.summary.provisions_called_cents} />} />
            <Stat label={s.summary.balance_cents >= 0 ? "Reste dû au syndic" : "Trop-versé au syndic"} value={<Amount cents={Math.abs(s.summary.balance_cents)} />} />
          </div>
          <Table caption="Postes du décompte">
            <thead>
              <tr>
                <th>Poste</th>
                <th className="right">Montant</th>
                <th className="right">Part récupérable</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.label}
                    <span className="finances-sous">{l.category_label}</span>
                  </td>
                  <td className="right"><Amount cents={l.amount_cents} /></td>
                  <td className="right">
                    <Amount cents={l.recoverable_cents} />
                    <span className="finances-sous">{l.recoverable_basis === "modifie" ? "modifiée par vous" : "proposée"}</span>
                  </td>
                  <td>{l.source_ref || "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <KeyValues items={[["Période", `du ${date(s.period_start)} au ${date(s.period_end)}`], ["Pièce", s.document_id ? <DocLink id={s.document_id} name={s.document_filename} /> : "Aucune"]]} />
          <div className="finances-actions">
            {s.status === "valide" ? (
              <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={org.api(`/condo/statements/${s.id}/regularisation.csv`)} download>
                Export pour la régularisation (CSV)
              </a>
            ) : null}
            {canEdit ? (
              <>
                <Button small variant="secondaire" onClick={() => setEditing(s)}>Modifier</Button>
                {s.status !== "valide" ? (
                  <Button small busy={a.busy} onClick={() => a.run(() => post(org.api(`/condo/statements/${s.id}/validate`)), { success: "Décompte validé.", refresh: [org.api("/condo")] })}>
                    Valider les parts récupérables
                  </Button>
                ) : null}
                <Button small variant="discret" onClick={() => a.run(() => del(org.api(`/condo/statements/${s.id}`)), { success: "Décompte supprimé.", refresh: [org.api("/condo")] })}>Supprimer</Button>
              </>
            ) : null}
          </div>
        </Card>
      ))}
      {editing ? <StatementModal statement={editing === "new" ? null : editing} categories={q.data?.line_categories ?? []} defaultBuilding={building} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

type LineForm = { label: string; category: string; amount_cents: number | null; recoverable_cents: number | null; source_ref: string; key: number };

function StatementModal({ statement, categories, defaultBuilding, onClose }: { statement: Statement | null; categories: { code: string; label: string; default_ratio_bp: number; hint: string }[]; defaultBuilding: string; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const s = statement;
  const y = currentYear() - 1;
  const [f, setF] = useState({
    building_id: s?.building_id ?? defaultBuilding,
    property_id: s?.property_id ?? "",
    fiscal_year: s?.fiscal_year ?? String(y),
    period_start: s?.period_start ?? `${y}-01-01`,
    period_end: s?.period_end ?? `${y}-12-31`,
    notes: s?.notes ?? "",
  });
  const [lines, setLines] = useState<LineForm[]>(
    s?.lines.map((l, i) => ({ label: l.label, category: l.category, amount_cents: l.amount_cents, recoverable_cents: l.recoverable_basis === "modifie" ? l.recoverable_cents : null, source_ref: l.source_ref, key: i })) ?? [{ label: "", category: "eau", amount_cents: null, recoverable_cents: null, source_ref: "", key: 0 }],
  );
  const a = useAction();
  const proposal = (l: LineForm) => {
    const c = categories.find((x) => x.code === l.category);
    return c && l.amount_cents !== null ? Math.round((l.amount_cents * c.default_ratio_bp) / 10000) : null;
  };
  const setLine = (i: number, patch: Partial<LineForm>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <Modal
      open
      onClose={onClose}
      title={s ? "Modifier le décompte" : "Décompte annuel du syndic"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const missing = required([
                [!!f.building_id, "building_id", "Choisissez l’immeuble."],
                [!!f.period_start && !!f.period_end, "period_end", "Indiquez les dates de l’exercice."],
              ]);
              if (missing) return a.setError(missing);
              const body = {
                ...f,
                property_id: f.property_id || null,
                lines: lines.map((l) => ({ label: l.label, category: l.category, amount_cents: l.amount_cents ?? -1, recoverable_cents: l.recoverable_cents, source_ref: l.source_ref })),
              };
              const r = await a.run(() => (s ? put(org.api(`/condo/statements/${s.id}`), body) : post(org.api("/condo/statements"), body)), { success: "Décompte enregistré, à valider.", refresh: [org.api("/condo")] });
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        {s?.status === "valide" ? <Notice tone="attention">Modifier un décompte validé le remet « à valider ».</Notice> : null}
        <ErrorBox error={a.error} />
        <div className="form-row">
          <BuildingSelect refs={refs.data} value={f.building_id} error={fieldError(a.error, "building_id")} onChange={(v) => setF({ ...f, building_id: v, property_id: "" })} empty="Choisir…" />
          <PropertySelect refs={refs.data} buildingId={f.building_id} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} label="Lot" empty="Tout l’immeuble" />
        </div>
        <div className="form-row">
          <Input label="Exercice" value={f.fiscal_year} error={fieldError(a.error, "fiscal_year")} onChange={(e) => setF({ ...f, fiscal_year: e.target.value })} />
          <Input label="Début" type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} />
          <Input label="Fin" type="date" value={f.period_end} error={fieldError(a.error, "period_end")} onChange={(e) => setF({ ...f, period_end: e.target.value })} />
        </div>
        <h3>Postes</h3>
        <div className="finances-lignes-decompte">
          {lines.map((l, i) => {
            const cat = categories.find((c) => c.code === l.category);
            const p = proposal(l);
            return (
              <div key={l.key} className="finances-ligne-decompte">
                <Input label="Libellé" value={l.label} error={fieldError(a.error, `lines.${i}.label`)} onChange={(e) => setLine(i, { label: e.target.value })} />
                <Select label="Nature" value={l.category} help={cat?.hint || undefined} onChange={(e) => setLine(i, { category: e.target.value })} options={categories.map((c) => ({ value: c.code, label: c.label }))} />
                <MoneyInput label="Montant" cents={l.amount_cents} error={fieldError(a.error, `lines.${i}.amount_cents`)} onChange={(c) => setLine(i, { amount_cents: c })} />
                <MoneyInput
                  key={`${l.key}-${l.category}`}
                  label="Part récupérable"
                  cents={l.recoverable_cents}
                  help={p !== null ? <>Proposition : <span className="finances-propose">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(p / 100)}</span> — laissez vide pour la retenir</> : "Laissez vide pour la proposition"}
                  error={fieldError(a.error, `lines.${i}.recoverable_cents`)}
                  onChange={(c) => setLine(i, { recoverable_cents: c })}
                />
                <Input label="Source (page, ligne)" value={l.source_ref} onChange={(e) => setLine(i, { source_ref: e.target.value })} />
                <Button variant="discret" small disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))}>Retirer</Button>
              </div>
            );
          })}
        </div>
        <div>
          <Button variant="secondaire" small onClick={() => setLines([...lines, { label: "", category: "entretien", amount_cents: null, recoverable_cents: null, source_ref: "", key: Date.now() }])}>
            Ajouter un poste
          </Button>
        </div>
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}
